<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { required } from '@vuelidate/validators';
import { useRouting, useValidation } from '@/composables';
import { SIS_PROVIDER_SLUG } from '@/enum';
import { IntegrationSchoolDayService } from '@/services';
import { useTenantIntegrationStore } from '@/store';
import SchoolDaySISOrganizations from './SchoolDaySISOrganizations.vue';

const tenantIntegrationStore = useTenantIntegrationStore();

const { error } = storeToRefs(tenantIntegrationStore);

const $routing = useRouting();

const $validation = useValidation();

const isLoading = computed(() => tenantIntegrationStore.loading);

const rules = {
  hostUrl: { required },
  hostAuthUrl: { required },
  clientId: { required },
  secretKey: { required },
  enableSync: { required: false },
  archiveAfter: { required },
};

const payload = ref({
  hostUrl: '',
  hostAuthUrl: '',
  clientId: '',
  secretKey: '',
  enableSync: false,
  archiveAfter: 30,
});

const $v = $validation.defineValidation(rules, payload);

const tenantId = ref(Number($routing.route.params.id));

const integration = ref();

const integrationStatus = ref(null);

const integrationStatusStyles = computed(() => {
  if (integrationStatus.value === null) return 'ml-2 text-lg text-gray';
  return `ml-2 text-lg ${integrationStatus.value ? 'text-green' : 'text-color-red'}`;
});

onMounted(async () => await init());

const init = async () => {
  defineIntegrationStatus();
  defineIntegration();
};

const defineIntegrationStatus = async () => {
  const status = await IntegrationSchoolDayService.status(tenantId.value);
  integrationStatus.value = status.data;
};

const defineIntegration = () => {
  integration.value = tenantIntegrationStore.findIntegration(SIS_PROVIDER_SLUG.SCHOOLDAY);

  if (integration.value) {
    payload.value = {
      id: integration.value.id,
      hostUrl: integration.value.data.host_url,
      hostAuthUrl: integration.value.data.host_auth_url,
      clientId: integration.value.data.client_id,
      secretKey: integration.value.data.secret_key,
      enableSync: integration.value.data.can_sync,
      archiveAfter: !integration.value.data.archive_after
        ? payload.value.archiveAfter
        : integration.value.data.archive_after,
    };
  }
};

const onCancel = () => {
  init();
  $validation.resetDirty();
};

const onSave = async () => {
  if ($v.value.$invalid) return;

  const formData = new FormData();
  const configData = JSON.stringify({
    host_url: payload.value.hostUrl,
    host_auth_url: payload.value.hostAuthUrl,
    client_id: payload.value.clientId,
    secret_key: payload.value.secretKey,
    can_sync: payload.value.enableSync,
    archive_after: payload.value.archiveAfter,
  });

  formData.append('data', configData);
  formData.append('integration_provider_type_slug', SIS_PROVIDER_SLUG.SCHOOLDAY);

  payload.value.id
    ? await tenantIntegrationStore.updateTenantIntegration(tenantId.value, payload.value.id, formData)
    : await tenantIntegrationStore.saveTenantIntegration(tenantId.value, formData);

  if (!error.value) {
    await init();
    $validation.resetDirty();
  }
};
</script>

<template>
  <Card>
    <template #header>
      <div class="flex">
        <h2 class="m-0">SchoolDay OneRoster</h2>
      </div>
      <div class="flex flex-grow-1"></div>
      <div class="flex">
        <span>Integration Status</span>
        <Icon name="circle" :class="integrationStatusStyles" />
      </div>
    </template>
    <template #content>
      <form id="schoolday_sis_form" class="w-full">
        <div class="field">
          <label for="host-url" class="required">Host URL</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <InputText
            v-else
            id="host-url"
            v-model="$v.hostUrl.$model"
            :class="$validation.fieldStyles('hostUrl', 'w-full')"
            placeholder="Enter Host URL"
            autocomplete="off"
          />
        </div>
        <div class="field">
          <label for="host-auth-url" class="required">Host Auth URL</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <InputText
            v-else
            id="host-auth-url"
            v-model="$v.hostAuthUrl.$model"
            :class="$validation.fieldStyles('hostAuthUrl', 'w-full')"
            placeholder="Enter Host Auth URL"
            autocomplete="off"
          />
        </div>
        <div class="field">
          <label for="client-id" class="required">Client ID</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <Password
            v-else
            id="client-id"
            v-model="$v.clientId.$model"
            :toggle-mask="true"
            :class="$validation.fieldStyles('clientId', 'w-full')"
            input-class="w-full"
            placeholder="Enter Client ID"
            :feedback="false"
            autocomplete="off"
          />
        </div>
        <div class="field">
          <label for="client-secret" class="required">Secret Key</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <Password
            v-else
            id="secret-key"
            v-model="$v.secretKey.$model"
            :toggle-mask="true"
            :class="$validation.fieldStyles('secretKey', 'w-full')"
            input-class="w-full"
            placeholder="Enter Secret Key"
            :feedback="false"
            autocomplete="off"
          />
        </div>
        <div class="flex field">
          <div class="flex flex-none my-2">
            <label for="enable-automated-data-synchronization">Enable Automated Data Synchronization</label>
          </div>
          <div class="flex flex-none mx-3">
            <InputSwitch v-model="$v.enableSync.$model" />
          </div>

          <div v-if="$v.enableSync.$model" class="flex">
            <div class="flex flex-none my-2">
              <label for="enable-automated-data-synchronization">Archive After</label>
            </div>
            <div class="flex flex-none mx-3">
              <Skeleton v-if="isLoading" height="2.3rem" />
              <InputNumber
                v-else
                id="archive-after"
                v-model="$v.archiveAfter.$model"
                :min="0"
                :max="50"
                :class="$validation.fieldStyles('archiveAfter')"
                placeholder="Days"
                autocomplete="off"
              />
              <span class="m-2" v-text="'Days'" />
            </div>
          </div>
        </div>
        <div
          v-if="$validation.anyDirty() && !tenantIntegrationStore.openModal"
          class="flex justify-content-end mt-4 pb-4 pl-2 pr-2"
        >
          <div class="flex w-full mt-3 mr-4 border-top-1 border-primary-500"></div>
          <AppButton type="secondary" class="mr-3" text="Cancel" @click="onCancel" />
          <AppButton type="primary" text="Save" @click="onSave" />
        </div>
        <div
          v-if="(integration && !$validation.anyDirty()) || tenantIntegrationStore.openModal"
          class="flex justify-content-end mt-4 pb-5 pl-2 pr-2"
        >
          <div class="flex w-full mt-3 border-top-1 border-primary-500"></div>
        </div>
      </form>
      <SchoolDaySISOrganizations v-if="integration" :integration="integration" />
    </template>
  </Card>
</template>

<style>
.text-green {
  color: #4caf50 !important;
}
</style>
